import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { buildRoute, ROUTES } from '../../constants/routes';

const PackageCard = ({ pkg, hideDiscount = false, hideBadge = false }) => {
  const navigate = useNavigate();
  const go = () => navigate(buildRoute(ROUTES.PACKAGE_DETAIL, { id: pkg.id }));
  const dark = pkg.featured && !hideBadge;

  return (
    <Box
      onClick={go}
      sx={{
        bgcolor: dark ? 'var(--forest)' : '#fff',
        border: '1.5px solid',
        borderColor: dark ? 'var(--forest)' : 'var(--border-l)',
        borderRadius: '20px',
        p: { xs: 2.25, md: 2.75 },
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.35s cubic-bezier(0.23,1,0.32,1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 36px rgba(21,42,30,0.14)',
          borderColor: dark ? 'var(--forest)' : 'var(--sage-p)',
        },
      }}
    >
      {/* Most Booked badge — top right */}
      {dark && !hideBadge && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'var(--amber)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 100,
            px: 1.125,
            py: 0.5,
            lineHeight: 1,
          }}
        >
          <Box component="span" sx={{ lineHeight: 1 }}>★</Box>
          <Box component="span" sx={{ lineHeight: 1 }}>Most Booked</Box>
        </Box>
      )}

      {/* Icon */}
      <Typography sx={{ fontSize: 32, mb: 1.5, lineHeight: 1 }}>{pkg.icon}</Typography>

      {/* Title */}
      <Typography
        className="serif"
        sx={{
          fontSize: { xs: 17, md: 19 },
          fontWeight: 600,
          fontStyle: 'italic',
          color: dark ? '#fff' : 'var(--ink)',
          lineHeight: 1.2,
          mb: 0.5,
          pr: dark ? 9 : 0,
        }}
      >
        {pkg.name}
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          fontSize: 12,
          color: dark ? 'rgba(255,255,255,0.5)' : 'var(--stone)',
          fontWeight: 400,
          mb: 1.5,
        }}
      >
        {pkg.paramCount} params &nbsp;·&nbsp; {pkg.tat}
      </Typography>

      {/* Price row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          className="serif"
          component="span"
          sx={{
            fontSize: { xs: 22, md: 26 },
            fontWeight: 700,
            color: dark ? '#fff' : 'var(--ink)',
            lineHeight: 1,
          }}
        >
          ₹{pkg.price.toLocaleString()}
        </Typography>
        {!hideDiscount && (
          <>
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                textDecoration: 'line-through',
                color: dark ? 'rgba(255,255,255,0.4)' : 'var(--stone)',
                lineHeight: 1,
              }}
            >
              ₹{pkg.original.toLocaleString()}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: dark ? 'var(--sage-l)' : 'var(--sage)',
                bgcolor: dark ? 'rgba(200,237,216,0.12)' : 'rgba(106,174,128,0.15)',
                borderRadius: 100,
                px: 0.875,
                py: 0.375,
                lineHeight: 1,
              }}
            >
              {pkg.savePercent} off
            </Typography>
          </>
        )}
      </Box>

      {/* Reports in — clock row */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1.75,
          lineHeight: 1,
        }}
      >
        <AccessTimeIcon
          sx={{
            fontSize: 12,
            color: dark ? 'rgba(255,255,255,0.4)' : 'var(--stone)',
            display: 'block',
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            color: dark ? 'rgba(255,255,255,0.5)' : 'var(--stone)',
            fontWeight: 500,
            lineHeight: 1,
          }}
        >
          Reports in {pkg.tat}
        </Typography>
      </Box>

      {/* View Details button */}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          go();
        }}
        sx={{
          mt: 'auto',
          width: '100%',
          bgcolor: dark ? 'var(--amber)' : 'var(--forest)',
          color: '#fff',
          borderRadius: 100,
          fontSize: 12.5,
          fontWeight: 700,
          textTransform: 'none',
          py: 1,
          '&:hover': {
            bgcolor: dark ? '#C77C1D' : 'var(--ink)',
          },
        }}
      >
        View Details
      </Button>
    </Box>
  );
};

export default PackageCard;
